import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api } from "../lib/api";
import { queryKeys } from "../lib/queryKeys";

/**
 * Everything an outgoing message will carry below what is being written: the
 * quoted thread, then the sign-off. Shown, and not edited.
 *
 * Read-only on purpose, and the reason generalises from the signature to the
 * quote without changing. Every complaint about the composer came from these
 * being live text in the writing surface: they pushed the message down to a
 * strip at the top, and a click meant for the end of a sentence landed in
 * them. Nobody was editing either -- they wanted to see that it was there.
 *
 * The quote is worse than the signature was, because it is somebody else's
 * words. A stray keystroke in a quoted thread sends the customer their own
 * message back altered, which is the kind of thing that gets noticed once and
 * never trusted again.
 *
 * ## Gmail's control, deliberately
 *
 * A small grey pill of three dots at the bottom of the compose box, opening the
 * trimmed content in place. Copied closely, because everyone on this desk uses
 * Gmail all day and a control they already know needs no explaining -- and
 * Gmail's pill hides exactly this pair, the quoted thread with the signature,
 * which is why one control holds both here rather than two sitting side by
 * side asking to be told apart.
 *
 * What that rules out, and what the first attempt got wrong: this is not a
 * panel. It has no border of its own, no "SIGNATURE" heading, no explanatory
 * caption, and it does not sit below the editor as a second box competing with
 * it. It lives *inside* the editor's border, under the writing surface, because
 * what it reveals is part of the message being written rather than information
 * about the message. The dots are small and quiet -- roughly 24 by 14 pixels --
 * so a composer with nothing expanded looks like a composer, not like a form
 * with a widget bolted underneath.
 *
 * Expanded, the signature is rendered plainly and inline, at its own size,
 * exactly where it will appear in the sent mail. An attempt to shrink the
 * images so more of it fitted collapsed the table layout most signatures are
 * built from and lost the logo altogether -- a preview that has rearranged the
 * thing it is previewing is worse than a short one.
 *
 * ## Why it renders when there is nothing to show
 *
 * An earlier version returned null for an agent with no signature configured,
 * on the grounds that an empty box implies something failed to load. That is
 * exactly backwards: "I can no longer see signatures on any draft" is what an
 * agent with no signature reports, because silence is indistinguishable from
 * breakage. The dots are always there, and opening them says so in one line --
 * the difference between a missing feature and a missing setting.
 */

/**
 * Remembered across composers and reloads.
 *
 * Deliberately one key rather than one per ticket: the question is "do I want
 * to see my signature", and answering it per conversation would mean answering
 * it again on every new one.
 */
const STORAGE_KEY = "pulse.signature.open";

function storedPreference(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    /* Storage is unavailable in some private modes. Defaulting to collapsed
       matches the common case; the toggle still works for the session. */
    return false;
  }
}

export function ComposerTrailer({
  /** Which alias's fallback applies, for an agent who has no signature. */
  channelId,
  /**
   * The thread this reply will quote, or null when it will quote nothing.
   *
   * Built by the composer when Reply was chosen and held there, so this only
   * ever renders it. Null is the ordinary case: typing into the box and
   * sending quotes nothing, exactly as it did before any of this.
   */
  quotedHtml,
}: {
  channelId: number | undefined;
  quotedHtml?: string | null;
}) {
  const [open, setOpen] = useState(storedPreference);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(open));
    } catch {
      /* See storedPreference -- a preference that cannot be saved is not worth
         failing a send over. */
    }
  }, [open]);

  /*
   * Resolved by the server so what is shown here and what is sent cannot drift
   * apart. Fetched for whichever alias is selected in From, which it can follow
   * exactly because it is no longer editable text: there is nothing an agent
   * might have changed for a refetch to overwrite.
   */
  const signature = useQuery({
    queryKey: queryKeys.mySignature(channelId),
    queryFn: () => api.mySignature(channelId),
    staleTime: 5 * 60_000,
  });

  const html = signature.data?.signatureHtml ?? null;

  return (
    <div className="px-3 pb-2">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-label={open ? "Hide trimmed content" : "Show trimmed content"}
        title={
          quotedHtml
            ? open
              ? "Hide the quoted thread and signature"
              : "Show the quoted thread and signature"
            : open
              ? "Hide signature"
              : "Show signature"
        }
        className="flex h-3.5 w-6 items-center justify-center gap-[3px] rounded bg-surface-2 hover:bg-line"
      >
        {/*
          Three dots drawn as dots rather than typed as "•••". The character
          renders at a different size and baseline in every font on every
          platform, and inside a 14-pixel pill that is the difference between
          Gmail's control and three specks sitting slightly too high.
        */}
        {[0, 1, 2].map((dot) => (
          <span
            key={dot}
            aria-hidden
            className="h-[3px] w-[3px] rounded-full bg-muted"
          />
        ))}
      </button>

      {open ? (
        <div className="mt-1.5 max-h-56 overflow-y-auto">
          {html ? (
            /*
              The agent's own HTML, resolved by /me/signature from what they
              saved in settings, and sanitised server-side on the way in.
              Rendered here rather than in a contenteditable so it cannot be
              typed into by accident -- which is the point of the whole thing.
            */
            <div
              aria-label="Your signature"
              /* `mail-colours`, like the thread's mail: a signature pasted out
                 of Gmail carries Gmail's black ink, unreadable in dark mode. */
              className="email-body mail-colours text-xs opacity-70"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          ) : (
            <p className="text-xs text-faint">
              No signature set. This will send without one.
            </p>
          )}

          {/*
            Under the signature, which is where it will be in the sent mail:
            body, sign-off, then the conversation it answers. Sanitised on the
            way out of the API like every other message body -- this is the same
            HTML the thread above is drawing.
          */}
          {quotedHtml ? (
            /* The rule sits on a wrapper because `mail-colours` inverts
               whatever its element paints, its own border included. */
            <div className="mt-2 border-t border-line pt-2">
              <div
                aria-label="The thread this reply will quote"
                className="email-body mail-colours text-xs opacity-70"
                dangerouslySetInnerHTML={{ __html: quotedHtml }}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
